import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import PortalHeader from '../../assessment-portal/components/portal-header';
import {
  MOBILE_PRIMARY_CTA_CLASS,
  MOBILE_PRIMARY_CTA_ENABLED_CLASS,
} from '../../assessment-portal/components/primary-cta';
import { QUESTIONNAIRE_BASE } from '../data/constants';
import { formatAppointmentWhen } from '../utils/appointment-display';

interface BookingConfirmationPageProps {
  firstName?: string
  clinicianName?: string
  clinicianTitle?: string
  startDateTime?: string
}

const exemptions = [
  { num: 1, label: 'ASD-only bookings' },
  { num: 2, label: 'Patients aged 15 years & under' },
] as const;

const BookingConfirmationPage: FC<BookingConfirmationPageProps> = ({
  firstName,
  clinicianName,
  clinicianTitle,
  startDateTime,
}) => {
  const navigate = useNavigate();

  const appointmentWhen = formatAppointmentWhen(startDateTime || '');
  const clinicianDisplay = clinicianName || 'Your clinician';

  const handleStartQuestionnaire = () => {
    navigate(QUESTIONNAIRE_BASE);
  };

  return (
    <div className="adhd-portal adhd-booked-page min-h-screen flex flex-col">
      <PortalHeader
        title="You're booked"
        subtitle="One more thing to do"
        chip="Confirmed"
        chipTone="green"
        progress={100}
      />

      <main className="adhd-conf-main flex-1">
        <div className="adhd-conf-hero">
          <div className="adhd-conf-check">
            <svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="#10B981" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="adhd-conf-bookt">
            You&apos;re booked
            {firstName ? (
              <>
                ,
                {' '}
                {firstName}
              </>
            ) : null}
          </h1>
          <p className="adhd-conf-booksub adhd-conf-booksub--desktop">
            Your intake assessment is confirmed.
          </p>
          <p className="adhd-conf-booksub adhd-conf-booksub--mobile">
            {appointmentWhen}
            <br />
            with
            {' '}
            {clinicianDisplay}
          </p>
        </div>

        <div className="adhd-conf-stack">
          <div className="adhd-conf-stepcard">
            <p className="adhd-conf-eyebrow">Do this now</p>
            <div className="adhd-conf-steptitle">
              <span className="adhd-conf-stepname">Complete your questionnaire</span>
              <span className="adhd-conf-reqbadge">Required for you</span>
            </div>
            <p className="adhd-conf-stepmeta">
              <strong>About 45 minutes in total.</strong>
              {' '}
              It&apos;s best to complete it in one sitting, at your own pace.
            </p>
            <div className="adhd-conf-infonote">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#0552F0" strokeWidth="1.9" aria-hidden>
                <circle cx="12" cy="12" r="9" />
                <path d="M12 11v5M12 7.5h.01" />
              </svg>
              <div className="adhd-conf-infotext">
                Finishing before your appointment means your psychiatrist can give
                you a full assessment on the day.
              </div>
            </div>
            <div className="adhd-conf-exempt">
              <span className="adhd-conf-exlabel">Exemptions — not required for</span>
              {exemptions.map((item) => (
                <div key={item.num} className="adhd-conf-exrow">
                  <span className="adhd-conf-exnum">{item.num}</span>
                  {item.label}
                </div>
              ))}
            </div>
            <div className="adhd-conf-actions adhd-conf-actions--desktop">
              <button
                type="button"
                onClick={handleStartQuestionnaire}
                className="adhd-conf-startbtn"
              >
                Get ready
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </button>
              <span className="adhd-conf-later">
                I&apos;ll do it later — we&apos;ll email you a link
              </span>
            </div>
          </div>

          <div className="adhd-conf-card">
            <div className="adhd-conf-asidehd">Appointment details</div>
            <div className="adhd-conf-arow">
              <span className="adhd-conf-ci">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <circle cx="12" cy="8" r="3.6" />
                  <path d="M5 20c0-3.7 3.1-6.2 7-6.2s7 2.5 7 6.2" />
                </svg>
              </span>
              <div>
                <div className="adhd-conf-clabel">{clinicianDisplay}</div>
                <div className="adhd-conf-csub">
                  {clinicianTitle || 'Mental Health Clinician · Intake'}
                </div>
              </div>
            </div>
            <div className="adhd-conf-arow adhd-conf-arow--bt">
              <span className="adhd-conf-ci">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
                  <path d="M3.5 9.5h17M8 3.5v3M16 3.5v3" />
                </svg>
              </span>
              <div>
                <div className="adhd-conf-clabel">{appointmentWhen}</div>
                <div className="adhd-conf-csub">Telehealth video · about 45 min</div>
              </div>
            </div>
          </div>

          <div className="adhd-conf-card">
            <div className="adhd-conf-cardhd">
              <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="#0552F0" strokeWidth="1.9" aria-hidden>
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
              What happens after
            </div>
            <div className="adhd-conf-tl">
              <div className="adhd-conf-tlitem">
                <span className="adhd-conf-tldot">
                  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L20 13l1 5v1a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2Z" />
                  </svg>
                </span>
                <span className="adhd-conf-tlline" aria-hidden />
                <div>
                  <div className="adhd-conf-tlname">Intake call (if needed)</div>
                  <div className="adhd-conf-tldesc">
                    If anything needs clarifying, our team will call you before your
                    appointment.
                  </div>
                </div>
              </div>
              <div className="adhd-conf-tlitem adhd-conf-tlitem--last">
                <span className="adhd-conf-tldot">
                  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <rect x="3" y="6" width="13" height="12" rx="2" />
                    <path d="M16 10l5-3v10l-5-3" />
                  </svg>
                </span>
                <div>
                  <div className="adhd-conf-tlname">Your appointment</div>
                  <div className="adhd-conf-tldesc">
                    {appointmentWhen}
                    . Join by video — your secure link is sent to your email &amp;
                    phone beforehand.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <div className="portal-navrow-spacer md:hidden" aria-hidden />
      <div className="portal-navrow--sticky md:hidden">
        <button
          type="button"
          onClick={handleStartQuestionnaire}
          className={`${MOBILE_PRIMARY_CTA_CLASS} ${MOBILE_PRIMARY_CTA_ENABLED_CLASS}`}
        >
          Get ready
        </button>
        <p className="adhd-conf-later-mobile">
          I&apos;ll do it later — we&apos;ll email you a link
        </p>
      </div>
    </div>
  );
};

export default BookingConfirmationPage;
